import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Checkbox,
  IconButton,
  Pagination,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import ReportRoundedIcon from "@mui/icons-material/ReportRounded";
import CreateNewFolderRoundedIcon from "@mui/icons-material/CreateNewFolderRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import GroupRoundedIcon from "@mui/icons-material/GroupRounded";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import moment from "moment";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import projectsData from "./projectsData.json";
import { projectHealthOf } from "./projectHealth";
import "./table.scss";
import { useToast } from "../Toast";
import { TableSkeleton } from "../../common/Skeleton";
import { ProjectContext } from "../../containers/Dashboard";
import { fetchAllProjects, setSelectedProjectBackLogId } from "../../common/Redux/Reducer";
import ActiveandInactiveModal from "../Modal/ActiveandInactiveModal";
import DeleteProjectModal from "../Modal/DeleteProjectModal";
import API_BASE_URL from "../config/appConfig";
import { ProjectAvatar, plainColorForProject } from "../../utilities/projectAvatars";

export { projectHealthOf } from "./projectHealth";

/* A status colour always ships with an icon and a label, never colour alone. */
const HEALTH_META = {
  "On Track": { className: "ph-ok", icon: <TrendingUpRoundedIcon />, label: "On track", rank: 1 },
  "At Risk": { className: "ph-risk", icon: <WarningAmberRoundedIcon />, label: "At risk", rank: 0 },
  Overdue: { className: "ph-overdue", icon: <ReportRoundedIcon />, label: "Overdue", rank: -1 },
};
const HEALTH_NONE = { className: "ph-none", icon: <RemoveRoundedIcon />, label: "No sprint", rank: 2 };

const headCells = projectsData.headCells;
const ROWS_PER_PAGE = 10;
// a multiple of the 2, 3 and 4 column tile layouts, so the last row is full
const TILES_PER_PAGE = 12;

/* the signed-in user from the access token; empty when there is none */
const currentUser = () => {
  try {
    const token = localStorage.getItem("accessToken");
    return token ? jwtDecode(token) : {};
  } catch (error) {
    return {};
  }
};

/* Mirrors the server's rule: the lead, or a member holding the "Project Owner"
   role. The server checks again, so this only decides what to show. */
const isOwnerOf = (project, user) => {
  if (!user?.id) return false;
  if (String(project.lead?._id || project.lead || "") === String(user.id)) return true;
  return (project.contributors || []).some(
    (member) =>
      String(member?.role || "").toLowerCase() === "project owner" &&
      (String(member?.employeeId || "") === String(user.id) || (user.email && member?.employeeEmail === user.email))
  );
};

/* the owner's mark when they have not uploaded a photo: the first letter of
   their first name on a flat colour, matching how a project without an avatar
   is drawn */
const firstLetterOf = (name = "") => String(name).trim().charAt(0).toUpperCase() || "?";

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("accessToken")}` });

/**
 * Owner photos, keyed by user id and by email.
 *
 * The projects endpoint populates the lead with a photo, but an older API build
 * sends only name and email, and then every owner would fall back to an initial
 * even though the photo exists on the user record. One extra call to the users
 * list fills that gap; a row's own `lead.profileImage` still wins when it comes
 * through, so this costs nothing once the API is up to date.
 */
const useOwnerPhotos = (projects) => {
  const [photos, setPhotos] = useState({});

  // only look the owners up once the list has landed, and only if one is missing a photo
  const needsLookup = useMemo(
    () => projects.some((project) => project.lead?.name && !project.lead?.profileImage),
    [projects]
  );

  useEffect(() => {
    if (!needsLookup) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/users`, { headers: authHeaders() });
        if (cancelled) return;
        const byKey = {};
        (Array.isArray(data?.users) ? data.users : []).forEach((user) => {
          if (!user?.profileImage) return;
          if (user._id) byKey[String(user._id)] = user.profileImage;
          if (user.email) byKey[String(user.email).toLowerCase()] = user.profileImage;
        });
        setPhotos(byKey);
      } catch (error) {
        /* the initial is a perfectly good fallback, so a failed lookup is not
           worth interrupting the list for */
        console.error("Could not load owner photos:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [needsLookup]);

  return (lead) =>
    lead?.profileImage ||
    photos[String(lead?._id || "")] ||
    photos[String(lead?.email || "").toLowerCase()] ||
    "";
};

/* "Company-Managed-Software" → "Company managed software" */
const prettyType = (type = "") => {
  const text = String(type).replace(/[-_]+/g, " ").trim().toLowerCase();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "—";
};

const isProjectActive = (project, switchState) => switchState[project._id] ?? project.active === "true";

function EnhancedTableHead({ order, orderBy, onRequestSort, rowCount, numSelected, onSelectAll }) {
  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" className="pl-check-cell">
          <Checkbox
            className="pl-check"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={(event) => onSelectAll(event.target.checked)}
            disabled={!rowCount}
            inputProps={{ "aria-label": "Select all projects" }}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell key={headCell.id} align="left" sortDirection={orderBy === headCell.id ? order : false}>
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={() => onRequestSort(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <span style={visuallyHidden}>{order === "desc" ? "sorted descending" : "sorted ascending"}</span>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell align="right" className="pl-actions-cell">
          <span style={visuallyHidden}>Actions</span>
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

export default function JiraProjectsTable({
  searchTerm = "",
  selectedProject,
  projects = [],
  loading,
  setSelectedProjectIds = () => {},
  selectedIds,
  statusFilter = "all",
  onCreateProject,
  view = "list",
}) {
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("title");
  // until someone sorts, active projects lead the list (the page's long-standing default)
  const [sortTouched, setSortTouched] = useState(false);
  const [internalSelected, setInternalSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUserData, setSelectedUserData] = useState(null);
  const [switchState, setSwitchState] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const user = useMemo(currentUser, []);
  const isTiles = view === "tiles";
  const perPage = isTiles ? TILES_PER_PAGE : ROWS_PER_PAGE;
  // the row being opened, so a slow backlog creation cannot be fired twice
  const [openingProjectId, setOpeningProjectId] = useState(null);
  /* backlogs created by the repair below, by project id. The list in the store
     still shows the project as having none, so without this a second click
     would ask the API for another one. */
  const repairedBacklogs = useRef({});
  const dispatch = useDispatch();
  const toast = useToast();
  const navigate = useNavigate();
  const { setBacklogId = () => {}, setSelectedProjectTitle = () => {} } = useContext(ProjectContext) || {};
  const ownerPhotoOf = useOwnerPhotos(projects);

  // the page can own the selection (so it can clear it); otherwise the table keeps its own
  const selected = Array.isArray(selectedIds) ? selectedIds : internalSelected;
  const setSelection = (next) => {
    setInternalSelected(next);
    setSelectedProjectIds(next);
  };

  useEffect(() => {
    const initialSwitchState = {};
    projects.forEach((project) => {
      initialSwitchState[project._id] = project.active === "true";
    });
    setSwitchState(initialSwitchState);
  }, [projects]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, statusFilter, selectedProject, projects.length, view]);

  const rows = useMemo(() => {
    let list = [...projects];
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      list = list.filter(
        (project) =>
          project.title?.toLowerCase().includes(term) ||
          project.key?.toLowerCase().includes(term) ||
          project.lead?.name?.toLowerCase().includes(term)
      );
    }
    if (selectedProject) {
      list = list.filter((project) => project.title === selectedProject);
    }
    if (statusFilter !== "all") {
      list = list.filter((project) => isProjectActive(project, switchState) === (statusFilter === "active"));
    }

    const valueOf = (project, key) => {
      switch (key) {
        case "lead":
          return (project.lead?.name || "").toLowerCase();
        case "health":
          return (HEALTH_META[projectHealthOf(project.sprintInfo)] || HEALTH_NONE).rank;
        case "status":
          return isProjectActive(project, switchState) ? 0 : 1;
        case "createdAt":
          return new Date(project.createdAt).getTime() || 0;
        default:
          return String(project[key] ?? "").toLowerCase();
      }
    };

    return list.sort((a, b) => {
      if (!sortTouched) {
        const activeGap = valueOf(a, "status") - valueOf(b, "status");
        if (activeGap) return activeGap;
      }
      const av = valueOf(a, orderBy);
      const bv = valueOf(b, orderBy);
      const compared = av > bv ? 1 : av < bv ? -1 : 0;
      return order === "asc" ? compared : -compared;
    });
  }, [projects, searchTerm, selectedProject, statusFilter, switchState, order, orderBy, sortTouched]);

  const pageCount = Math.max(1, Math.ceil(rows.length / perPage));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * perPage, safePage * perPage + perPage);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setSortTouched(true);
  };

  const handleSelectAll = (checked) => {
    setSelection(checked ? rows.map((project) => project._id) : []);
  };

  const toggleProject = (projectId) => {
    setSelection(selected.includes(projectId) ? selected.filter((id) => id !== projectId) : [...selected, projectId]);
  };

  /* A project is created and its backlog is created by a second call, so a
     project whose follow-up call never landed has an empty `backlogs` and no
     way for the user to fix it. Make the missing backlog here rather than
     turning the click into a dead end. */
  const ensureBacklogId = async (row) => {
    const existing = row?.backlogs?.[0] || repairedBacklogs.current[row?._id];
    if (existing) return existing;
    const { data } = await axios.post(`${API_BASE_URL}/backlogs/createBacklog`, { projectId: row?._id });
    if (data?._id) repairedBacklogs.current[row?._id] = data._id;
    return data?._id;
  };

  const openProject = async (row) => {
    if (openingProjectId) return;
    setOpeningProjectId(row?._id);
    try {
      const backlogId = await ensureBacklogId(row);
      if (!backlogId) throw new Error("The project has no backlog and one could not be created");
      dispatch(setSelectedProjectBackLogId({ id: backlogId }));
      setBacklogId(backlogId);
      setSelectedProjectTitle(row?.title);
      navigate(`/projects/${row?.slugString}/active-sprints`, {
        state: {
          queryParams: { projectId: row?._id, backlogId, projectName: row?.title, projectslug: row?.slugString },
        },
      });
    } catch (error) {
      console.error("Could not open project:", error);
      toast.error("Could not open this project. Please try again.");
    } finally {
      setOpeningProjectId(null);
    }
  };

  const handleActive = async (id) => {
    const newStatus = !switchState[id];
    try {
      const response = await axios.put(`${API_BASE_URL}/projects/updateProject/${id}`, { active: newStatus });
      if (response.status === 200) {
        setSwitchState((previous) => ({ ...previous, [id]: newStatus }));
        setSelectedUserData(null);
        setIsModalOpen(false);
        toast.success(newStatus ? "Project marked active" : "Project marked inactive");
      } else {
        console.error("Failed to update project status");
        toast.error("Could not change the project status");
      }
    } catch (error) {
      console.error("Error updating project status:", error);
      toast.error("Could not change the project status");
    }
  };

  const handleDeleted = (project) => {
    setDeleteTarget(null);
    setSelection(selected.filter((id) => id !== project._id));
    toast.success(`${project.title} was deleted`);
    dispatch(fetchAllProjects(authHeaders()));
  };

  const openStatusModal = (project) => {
    setSelectedUserData(project);
    setIsModalOpen(true);
  };

  /* Only the owner sees the action. It stays disabled while the project is
     active: deactivating is the first, deliberate step of a delete. */
  const renderDeleteAction = (project, active) => {
    if (!isOwnerOf(project, user)) return null;
    return (
      <Tooltip title={active ? "Deactivate the project before deleting it" : `Delete ${project.title}`}>
        <span className="pl-delete-wrap">
          <IconButton
            className="pl-delete"
            size="small"
            disabled={Boolean(active)}
            onClick={() => setDeleteTarget(project)}
            aria-label={`Delete ${project.title}`}
          >
            <DeleteOutlineRoundedIcon />
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const renderOwner = (lead) =>
    lead?.name ? (
      <Tooltip title={lead.email || lead.name}>
        <span className="pl-owner">
          <span className="pl-owner__avatar" style={{ background: plainColorForProject(lead.name) }} aria-hidden="true">
            {ownerPhotoOf(lead) ? <img src={ownerPhotoOf(lead)} alt="" className="pl-owner__photo" /> : firstLetterOf(lead.name)}
          </span>
          <span className="pl-owner__name">{lead.name}</span>
        </span>
      </Tooltip>
    ) : (
      <span className="pl-muted">No owner</span>
    );

  const renderStatus = (project, active) => (
    <label className={`pl-status ${active ? "is-active" : ""}`}>
      <Switch
        className="pl-switch"
        checked={Boolean(active)}
        onChange={() => openStatusModal(project)}
        inputProps={{ "aria-label": `${project.title} is ${active ? "active" : "inactive"}` }}
      />
      <span>{active ? "Active" : "Inactive"}</span>
    </label>
  );

  const renderProjectName = (project, active) => (
    <button
      type="button"
      className={`project-title-cls pl-project__name ${active ? "" : "inactive-project"}`}
      onClick={() => openProject(project)}
      disabled={!active}
      title={active ? `Open ${project.title}` : `${project.title} is inactive`}
    >
      {project.title}
    </button>
  );

  const emptyState =
    projects.length === 0 ? (
      <div className="pl-empty">
        <span className="pl-empty__icon">
          <CreateNewFolderRoundedIcon />
        </span>
        <strong>No projects yet</strong>
        <span>Create a project to start planning sprints with your team.</span>
        {onCreateProject ? (
          <Button className="pl-btn pl-btn--primary" onClick={onCreateProject} disableRipple>
            Create project
          </Button>
        ) : null}
      </div>
    ) : (
      <div className="pl-empty">
        <span className="pl-empty__icon">
          <SearchOffRoundedIcon />
        </span>
        <strong>No projects match</strong>
        <span>Try a different name, key or owner, or change the status filter.</span>
      </div>
    );

  if (loading) {
    if (isTiles) {
      return (
        <div className="pl-tiles" aria-busy="true">
          {Array.from({ length: 6 }, (_, index) => (
            <div key={index} className="pl-tile is-skeleton" />
          ))}
        </div>
      );
    }
    return (
      <TableSkeleton
        columns={headCells.map((cell) => cell.label)}
        rows={ROWS_PER_PAGE}
        showCheckbox
        avatarColumns={[headCells.findIndex((cell) => cell.label === "Project Owner")]}
      />
    );
  }

  const first = rows.length ? safePage * perPage + 1 : 0;
  const last = Math.min(rows.length, (safePage + 1) * perPage);

  const tilesView = (
    <div className="pl-tiles">
      {pageRows.map((row, index) => {
        const isItemSelected = selected.includes(row._id);
        const active = isProjectActive(row, switchState);
        const health = HEALTH_META[projectHealthOf(row?.sprintInfo)] || HEALTH_NONE;
        const members = Array.isArray(row.contributors) ? row.contributors.length : 0;
        const sprint = row?.sprintInfo?._id ? row.sprintInfo : null;
        const sprintTotal = sprint?.totalIssues || 0;
        const sprintDone = sprint?.IssuesDone || 0;
        const sprintPercent = sprintTotal ? Math.round((sprintDone * 100) / sprintTotal) : 0;
        const daysLeft = sprint?.endDate ? moment(sprint.endDate).endOf("day").diff(moment(), "days") : null;

        return (
          <article
            key={row._id}
            className={`pl-tile ${isItemSelected ? "is-selected" : ""} ${active ? "" : "is-inactive"}`}
            style={{ "--d": `${Math.min(index, 12) * 35}ms` }}
          >
            <header className="pl-tile__head">
              <ProjectAvatar avatar={row.avatar} title={row.title} className="pl-project__mark" />
              <span className="pl-tile__titles">
                {renderProjectName(row, active)}
                <span className="pl-tile__sub">
                  <span className="pl-key" title={row.key || ""}>{row.key || "—"}</span>
                  <span className="pl-type" title={prettyType(row.type)}>{prettyType(row.type)}</span>
                </span>
              </span>
              <Checkbox
                className="pl-check pl-tile__check"
                checked={isItemSelected}
                onChange={() => toggleProject(row._id)}
                inputProps={{ "aria-label": `Select ${row.title}` }}
              />
            </header>

            <div className={`pl-tile__sprint ${sprint ? "" : "is-empty"}`}>
              <div className="pl-tile__sprint-top">
                <span className={`ph-pill ${health.className}`}>
                  {health.icon}
                  {health.label}
                </span>
                {sprint ? (
                  <span className="pl-tile__sprint-name" title={sprint.name}>
                    {sprint.name}
                  </span>
                ) : null}
              </div>
              {sprint ? (
                <>
                  <span
                    className="pl-tile__bar"
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={sprintPercent}
                    aria-label={`${sprint.name} progress`}
                  >
                    <span style={{ width: `${sprintPercent}%` }} />
                  </span>
                  <span className="pl-tile__sprint-meta">
                    <span>
                      {sprintDone}/{sprintTotal} done
                    </span>
                    {daysLeft !== null ? (
                      <span className={daysLeft < 0 && sprintDone < sprintTotal ? "is-late" : ""}>
                        {daysLeft >= 0
                          ? `${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`
                          : `Ended ${moment(sprint.endDate).format("DD MMM")}`}
                      </span>
                    ) : null}
                  </span>
                </>
              ) : (
                <>
                  {/* an empty track keeps every tile's rows level with its neighbours */}
                  <span className="pl-tile__bar is-empty" aria-hidden="true" />
                  <span className="pl-tile__sprint-meta">
                    <span>No sprint running</span>
                  </span>
                </>
              )}
            </div>

            <div className="pl-tile__facts">
              <span>
                <GroupRoundedIcon />
                {members} {members === 1 ? "member" : "members"}
              </span>
              <Tooltip title={row.createdAt ? moment(row.createdAt).fromNow() : ""}>
                <span>
                  <CalendarTodayRoundedIcon />
                  {row.createdAt ? moment(row.createdAt).format("DD MMM YYYY") : "—"}
                </span>
              </Tooltip>
            </div>

            <footer className="pl-tile__foot">
              {renderOwner(row.lead)}
              <span className="pl-tile__actions">
                {renderStatus(row, active)}
                {renderDeleteAction(row, active)}
              </span>
            </footer>
          </article>
        );
      })}

      {rows.length === 0 ? <div className="pl-tiles__empty">{emptyState}</div> : null}
    </div>
  );

  const listView = (
    <TableContainer className="table-container">
      <Table aria-label="Projects" id="projects">
        <EnhancedTableHead
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          rowCount={rows.length}
          numSelected={rows.filter((project) => selected.includes(project._id)).length}
          onSelectAll={handleSelectAll}
        />
        <TableBody>
          {pageRows.map((row, index) => {
            const isItemSelected = selected.includes(row._id);
            const active = isProjectActive(row, switchState);
            const health = HEALTH_META[projectHealthOf(row?.sprintInfo)] || HEALTH_NONE;
            const members = Array.isArray(row.contributors) ? row.contributors.length : 0;

            return (
              <TableRow
                hover
                key={row._id}
                selected={isItemSelected}
                className={`pl-row ${active ? "" : "is-inactive"}`}
                style={{ "--d": `${Math.min(index, 10) * 35}ms` }}
              >
                <TableCell padding="checkbox" className="pl-check-cell">
                  <Checkbox
                    className="pl-check"
                    checked={isItemSelected}
                    onChange={() => toggleProject(row._id)}
                    inputProps={{ "aria-label": `Select ${row.title}` }}
                  />
                </TableCell>

                <TableCell align="left">
                  <div className="pl-project">
                    <ProjectAvatar avatar={row.avatar} title={row.title} className="pl-project__mark" />
                    <span className="pl-project__text">
                      {renderProjectName(row, active)}
                      <span className="pl-project__meta">
                        {members} {members === 1 ? "member" : "members"}
                      </span>
                    </span>
                  </div>
                </TableCell>

                <TableCell align="left">
                  <span className="pl-key" title={row.key || ""}>{row.key || "—"}</span>
                </TableCell>

                <TableCell align="left">
                  <span className={`ph-pill ${health.className}`}>
                    {health.icon}
                    {health.label}
                  </span>
                </TableCell>

                <TableCell align="left">
                  <span className="pl-type" title={prettyType(row.type)}>{prettyType(row.type)}</span>
                </TableCell>

                <TableCell align="left">
                  <Tooltip title={row.createdAt ? moment(row.createdAt).fromNow() : ""}>
                    <span className="pl-date">{row.createdAt ? moment(row.createdAt).format("DD MMM YYYY") : "—"}</span>
                  </Tooltip>
                </TableCell>

                <TableCell align="left">{renderOwner(row.lead)}</TableCell>

                <TableCell align="left" className="pl-status-cell">
                  {renderStatus(row, active)}
                </TableCell>

                <TableCell align="right" className="pl-actions-cell">
                  {renderDeleteAction(row, active)}
                </TableCell>
              </TableRow>
            );
          })}

          {rows.length === 0 && (
            <TableRow className="pl-empty-row">
              <TableCell colSpan={headCells.length + 2}>{emptyState}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <div className="pl-table">
      {isTiles ? tilesView : listView}

      {rows.length > 0 && (
        <div className="pl-table__foot">
          <span className="pl-muted">
            Showing {first}–{last} of {rows.length}
          </span>
          {pageCount > 1 ? (
            <Pagination
              className="table-pagination-container-cls"
              count={pageCount}
              page={safePage + 1}
              onChange={(event, next) => setPage(next - 1)}
              shape="rounded"
            />
          ) : null}
        </div>
      )}

      {selectedUserData && (
        <ActiveandInactiveModal
          open={isModalOpen}
          toggleModal={() => setIsModalOpen(!isModalOpen)}
          handleActive={handleActive}
          projectData={selectedUserData}
          switchState={switchState}
        />
      )}

      <DeleteProjectModal
        open={Boolean(deleteTarget)}
        projectData={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={handleDeleted}
      />
    </div>
  );
}

JiraProjectsTable.propTypes = {
  searchTerm: PropTypes.string,
  selectedProject: PropTypes.string,
  statusFilter: PropTypes.oneOf(["all", "active", "inactive"]),
  view: PropTypes.oneOf(["list", "tiles"]),
};
